import { useCallback, useEffect, useRef } from 'react';
import { useMap, useMapEvents } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet.heat';

const API_BASE = process.env.REACT_APP_API_URL || '/api/v1';

// Smooth gradients with low-end stops so faint regions fade gracefully
// instead of jumping straight to a fully-saturated band. The previous
// gradient started at 0.2, leaving a sharp yellow rim where intensity
// crossed that threshold.
const LAYER_COLORS = {
  wildfire: {
    gradient: {
      0.0: 'rgba(255,255,178,0)',
      0.15: '#fed976',
      0.35: '#fd8d3c',
      0.6: '#e31a1c',
      0.85: '#bd0026',
      1.0: '#800026',
    },
  },
  flood: {
    gradient: {
      0.0: 'rgba(222,235,247,0)',
      0.15: '#c6dbef',
      0.35: '#6baed6',
      0.6: '#2171b5',
      0.85: '#08519c',
      1.0: '#08306b',
    },
  },
};

function HeatmapLayer({
  layer = 'gold',
  enabled = false,
  // Optional UI overrides for the L.heatLayer render parameters.
  // When any of these are non-null, the corresponding zoom-adaptive
  // default is bypassed in favor of the explicit value. This lets a
  // parent (MapView) expose live sliders without sacrificing the
  // existing default behavior when no override is supplied.
  radius: radiusOverride = null,
  blur: blurOverride = null,
  max: maxOverride = null,
  minOpacity: minOpacityOverride = null,
}) {
  const map = useMap();
  const heatRef = useRef(null);
  const abortRef = useRef(null);

  // useCallback over the component-scope identifiers (layer, enabled,
  // map) so a layer-prop change rebinds the moveend/zoomend handlers
  // and re-runs the load-on-mount effect with the new closure. The
  // earlier in-line function captured the first-render value of
  // `layer`/`enabled` and the eslint-disable was hiding it. (#81 M1)
  const loadData = useCallback(() => {
    if (!enabled || !map) return;

    if (abortRef.current) abortRef.current.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    const bounds = map.getBounds();
    const bbox = `${bounds.getWest()},${bounds.getSouth()},${bounds.getEast()},${bounds.getNorth()}`;

    // Layer variants:
    //   wildfire                       → USFS WHP burn-prob (#91)
    //   wildfire-loss                  → FEMA NRI loss-weighted (legacy)
    //   wildfire-freq / flood-freq    → AFREQ (annualized frequency)
    //   <commodity>-mines              → USGS MRDS only
    //   <commodity>-prob               → ML probability only
    //   <commodity>                    → legacy combined (#89)
    //
    // Send the layer name verbatim to the backend; the suffix-based
    // routing happens server-side. Locally we only need to know the
    // *base* commodity/hazard for gradient + radius decisions.
    const isFreq = layer.endsWith('-freq');
    let baseLayer = layer;
    for (const suffix of ['-freq', '-loss', '-mines', '-prob']) {
      if (baseLayer.endsWith(suffix)) {
        baseLayer = baseLayer.slice(0, -suffix.length);
        break;
      }
    }
    const metric = isFreq ? 'frequency' : 'risk';

    // Scale the limit smoothly with zoom level. Earlier code used
    // hard buckets (1500/3000/6000 for hazard) which produced a
    // visible 2×–5× point-count jump at the bucket boundaries — the
    // user reported this as the heatmap "changing coverage" while
    // zooming. Now: smooth linear ramp clamped to a sane range.
    //
    // Hazard layers need fewer points than commodity because the
    // server-side 3×3 grid multiplies each tract by 9.
    const zoom = map.getZoom();
    const isHazard = baseLayer === 'wildfire' || baseLayer === 'flood';
    const lerp = (z, lo, hi, zlo = 3, zhi = 12) =>
      Math.round(lo + (hi - lo) * Math.min(1, Math.max(0, (z - zlo) / (zhi - zlo))));
    const limit = isHazard ? lerp(zoom, 1500, 6000) : lerp(zoom, 3000, 10000);

    // For the *frequency* variants the backend uses the metric flag
    // and the bare base name; for *-mines / *-prob* the backend
    // expects the full layer name with its suffix.
    const apiLayer = isFreq ? baseLayer : layer;
    fetch(`${API_BASE}/parcels/heatmap-points?bbox=${bbox}&layer=${apiLayer}&metric=${metric}&limit=${limit}&zoom=${zoom}`, {
      signal: controller.signal,
    })
      .then(r => r.json())
      .then(data => {
        if (!data.points || !data.points.length) {
          if (heatRef.current) { map.removeLayer(heatRef.current); heatRef.current = null; }
          return;
        }

        // Look up gradient by the base layer name — frequency variants
        // share the same color palette as their loss-risk counterparts.
        const config = LAYER_COLORS[baseLayer] || LAYER_COLORS[layer] || {};
        const isHazard = baseLayer === 'wildfire' || baseLayer === 'flood';

        // Per-zoom radius/blur scaling.
        //
        // leaflet.heat takes a pixel radius. A fixed pixel radius
        // covers wildly different geographic areas across zooms.
        // We anchor at zoom 6 with sane defaults and scale gently
        // upward (1.15× per level, not 1.25× — the earlier value
        // produced one-blob-fills-the-screen at z=12+). Capped
        // tightly so high-zoom views can actually see individual
        // hot spots instead of a soft saturation.
        //
        // Numbers: z=6 hazard=55, z=10 hazard=96, z=12 cap=110,
        //          z=14 cap=110.  z=6 commodity=28, z=14 cap=64.
        const ANCHOR_ZOOM = 6;
        const HAZARD_BASE_R = 55;
        const COMMODITY_BASE_R = 28;
        const baseR = isHazard ? HAZARD_BASE_R : COMMODITY_BASE_R;
        const capR = isHazard ? 110 : 64;
        const scale = Math.pow(1.15, Math.max(-3, Math.min(8, zoom - ANCHOR_ZOOM)));
        const computedRadius = Math.min(capR, Math.max(15, Math.round(baseR * scale)));
        // Blur ratio: tighter on hazard so individual tracts stay
        // visible at high zoom; softer on commodity to bridge sparse
        // parcel-level points.
        const computedBlur = Math.round(computedRadius * (isHazard ? 1.25 : 1.5));

        // UI overrides win when supplied; otherwise fall back to the
        // zoom-adaptive defaults above. `null` means "no override".
        const radius = radiusOverride != null ? radiusOverride : computedRadius;
        const blur = blurOverride != null ? blurOverride : computedBlur;
        const max = maxOverride != null ? maxOverride : (isHazard ? 3.0 : 1.0);
        const minOpacity = minOpacityOverride != null ? minOpacityOverride : 0.08;

        const options = {
          radius,
          blur,
          // maxZoom controls leaflet.heat's intensity-vs-zoom curve.
          // 13 (was 10) keeps hazard heat fully visible through
          // county-scale viewing — important because at z=11-12 the
          // user is comparing parcels and needs to see the layer.
          maxZoom: isHazard ? 13 : 14,
          max,           // small >1 to dim point pile-ups (hazard default 3.0)
          minOpacity,
          ...(config.gradient ? { gradient: config.gradient } : {}),
        };

        if (heatRef.current) {
          heatRef.current.setLatLngs(data.points);
        } else {
          heatRef.current = L.heatLayer(data.points, options).addTo(map);
        }
      })
      .catch(() => {});
  }, [enabled, map, layer, radiusOverride, blurOverride, maxOverride, minOpacityOverride]);

  useMapEvents({
    moveend: loadData,
    zoomend: loadData,
  });

  useEffect(() => {
    // When switching layers (e.g. wildfire → flood) we must tear down
    // the existing heat layer before reloading, otherwise loadData's
    // setLatLngs() branch reuses the old layer which was initialized
    // with the previous layer's gradient/radius/blur options — the
    // flood data would render with wildfire colors.
    //
    // The cleanup function below handles the teardown: React always
    // runs the previous effect's cleanup BEFORE running the new
    // effect on a dep change, so by the time the body runs again
    // heatRef.current is already null.
    if (enabled) {
      loadData();
    }
    return () => {
      if (heatRef.current) { map.removeLayer(heatRef.current); heatRef.current = null; }
      if (abortRef.current) abortRef.current.abort();
    };
  }, [enabled, layer, loadData, map]);

  return null;
}

export default HeatmapLayer;
